import { SalonIcon } from "./SalonIcon";
import type { MouseEvent } from "react";
import type { SalonMercadoPagoConnection, SalonOverview } from "./types";

function localDateKey(value: string, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en", {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).formatToParts(new Date(value));
  const part = (type: "year" | "month" | "day") => parts.find((item) => item.type === type)?.value ?? "";
  return `${part("year")}-${part("month")}-${part("day")}`;
}

function todayKey(timeZone: string): string {
  return localDateKey(new Date().toISOString(), timeZone);
}

function timeOnly(value: string, timeZone: string): string {
  return new Intl.DateTimeFormat("es-AR", {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(value));
}

function shortDateTime(value: string, timeZone: string): string {
  return new Intl.DateTimeFormat("es-AR", {
    timeZone,
    weekday: "short",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(value));
}

function countLabel(value: number, singular: string, plural: string): string {
  return `${value} ${value === 1 ? singular : plural}`;
}

function money(value: number, currency: string): string {
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value / 100);
}

export function SalonsHome({
  overview,
  organizationName,
  mercadoPago,
  mercadoPagoError,
  navigate,
  basePath,
  onRefresh,
  refreshing,
}: {
  overview: SalonOverview;
  organizationName: string;
  mercadoPago: SalonMercadoPagoConnection | null;
  mercadoPagoError: string | null;
  navigate: (path: string) => void;
  basePath?: string;
  onRefresh: () => void;
  refreshing: boolean;
}) {
  const base = basePath ?? window.location.pathname.match(/^(\/o\/[^/]+\/salons)/)?.[1] ?? "";
  const currentToday = todayKey(overview.timeZone);
  const todayAppointments = [...overview.appointments]
    .filter((item) => item.status !== "cancelled" && localDateKey(item.startsAt, overview.timeZone) === currentToday)
    .sort((left, right) => Date.parse(left.startsAt) - Date.parse(right.startsAt));
  const openConversations = [...overview.conversations]
    .filter((item) => item.status === "open")
    .sort((left, right) => Date.parse(right.updatedAt) - Date.parse(left.updatedAt));
  const todayConfirmedAppointments = todayAppointments.filter((item) => item.status === "confirmed");
  const upcoming = [...overview.appointments]
    .filter((item) =>
      item.status === "confirmed" &&
      Date.parse(item.endsAt) >= Date.now() &&
      localDateKey(item.startsAt, overview.timeZone) !== currentToday
    )
    .sort((left, right) => Date.parse(left.startsAt) - Date.parse(right.startsAt));

  const mercadoPagoStatus = mercadoPago?.connection?.status ?? null;
  const mercadoPagoNeedsAttention = Boolean(
    mercadoPagoError ||
    (mercadoPago?.connected && (!mercadoPago.runtimeReady || mercadoPagoStatus !== "active")),
  );
  const mercadoPagoLabel = mercadoPagoError
    ? "No se pudo verificar Mercado Pago"
    : !mercadoPago
      ? "Estado de Mercado Pago no disponible"
      : mercadoPago.connected && mercadoPago.runtimeReady && mercadoPagoStatus === "active"
        ? "Mercado Pago conectado"
        : mercadoPago.connected
          ? "Mercado Pago requiere atención"
          : mercadoPago.runtimeReady
            ? "Mercado Pago disponible · no conectado"
            : "Mercado Pago no configurado";

  const primary = mercadoPagoNeedsAttention
    ? {
        value: 1,
        meta: "integración de pagos requiere atención",
        reason: mercadoPagoError ?? "La conexión de Mercado Pago está degradada y puede afectar nuevos cobros.",
        label: "Revisar pagos",
        path: `${base}/sales`,
      }
    : openConversations.length > 0
    ? {
        value: openConversations.length,
        meta: countLabel(openConversations.length, "conversación abierta", "conversaciones abiertas"),
        reason: `Hay ${countLabel(openConversations.length, "conversación abierta", "conversaciones abiertas")} en el negocio.`,
        label: "Ver conversaciones",
        path: `${base}/conversations`,
      }
    : todayConfirmedAppointments.length > 0
      ? {
          value: todayConfirmedAppointments.length,
          meta: countLabel(todayConfirmedAppointments.length, "turno confirmado hoy", "turnos confirmados hoy"),
          reason: `Hay ${countLabel(todayConfirmedAppointments.length, "turno confirmado", "turnos confirmados")} para hoy.`,
          label: "Ver agenda",
          path: `${base}/agenda`,
        }
      : upcoming.length > 0
        ? {
            value: upcoming.length,
            meta: countLabel(upcoming.length, "próximo turno", "próximos turnos"),
            reason: "No hay urgencias abiertas. La agenda tiene próximos turnos confirmados.",
            label: "Ver agenda",
            path: `${base}/agenda`,
          }
        : {
            value: 0,
            meta: "Sin urgencias detectadas",
            reason: "No hay conversaciones abiertas ni turnos pendientes que requieran atención inmediata.",
            label: "Ver clientes",
            path: `${base}/customers`,
          };

  const priorities = [
    mercadoPagoNeedsAttention ? {
      key: "mercadopago-health",
      badge: "Atención",
      title: "Mercado Pago requiere revisión",
      description: mercadoPagoError ?? "La conexión está degradada; revisá el provider antes de iniciar nuevos cobros.",
      path: `${base}/sales`,
      cta: "Revisar pagos",
      tone: "urgent",
    } : null,
    openConversations.length > 0 ? {
      key: "conversations",
      badge: "Atención",
      title: countLabel(openConversations.length, "conversación abierta", "conversaciones abiertas"),
      description: "Abrí las conversaciones activas y continuá desde la conversación canónica.",
      path: `${base}/conversations`,
      cta: "Abrir conversaciones",
      tone: "urgent",
    } : null,
    todayConfirmedAppointments.length > 0 ? {
      key: "agenda-today",
      badge: "Hoy",
      title: countLabel(todayConfirmedAppointments.length, "turno confirmado", "turnos confirmados"),
      description: "Consultá horarios, clientes y servicios de la jornada.",
      path: `${base}/agenda`,
      cta: "Abrir agenda",
      tone: "normal",
    } : null,
    upcoming.length > 0 ? {
      key: "agenda-upcoming",
      badge: "Próximos",
      title: countLabel(upcoming.length, "turno confirmado", "turnos confirmados"),
      description: `El próximo es ${shortDateTime(upcoming[0]!.startsAt, overview.timeZone)}.`,
      path: `${base}/agenda`,
      cta: "Ver próximos",
      tone: "normal",
    } : null,
    overview.metrics.salesPendingCount > 0 ? {
      key: "sales-pending",
      badge: "Cobros",
      title: countLabel(overview.metrics.salesPendingCount, "venta pendiente", "ventas pendientes"),
      description: "Hay cobros pendientes registrados en el ledger de Ventas.",
      path: `${base}/sales`,
      cta: "Abrir Ventas",
      tone: "normal",
    } : null,
  ].filter((item): item is NonNullable<typeof item> => item !== null);

  function link(event: MouseEvent<HTMLAnchorElement>, path: string) {
    if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) return;
    event.preventDefault();
    navigate(path);
  }

  return <section className="cd-home-cockpit cd-home-cockpit--canon" aria-labelledby="salons-home-heading">
    <div className="cd-page-frame">
      <header className="cd-page-header cd-home-cockpit__page-header">
        <div>
          <p className="cd-page-header__eyebrow">Inicio operativo</p>
          <h1 id="salons-home-heading" className="cd-page-header__title">¡Hola! <span className="sr-only">Atención inmediata</span></h1>
          <p className="cd-page-header__description">Aquí tenés un resumen de la actividad de hoy en {organizationName}.</p>
          <div className="cd-home-cockpit__source-row">
            <span className="cd-home-cockpit__source">
              <span className="cd-home-cockpit__source-dot cd-home-cockpit__source-dot--live" aria-hidden="true" />
              <span>Datos del negocio</span>
            </span>
            <span className="cd-home-cockpit__source-hint">Agenda, conversaciones, clientes y Ventas · {organizationName}</span>
          </div>
        </div>
        <div className="cd-page-header__actions"><time className="cd-salons-date">{new Intl.DateTimeFormat("es-AR", { timeZone: overview.timeZone, weekday: "long", day: "numeric", month: "long" }).format(new Date())}</time>
          <button type="button" className="cd-button cd-button--ghost" onClick={onRefresh} disabled={refreshing} aria-busy={refreshing || undefined}>
            {refreshing ? "Actualizando…" : "Actualizar"}
          </button>
          <a href={primary.path} className="cd-button cd-button--primary" onClick={(event) => link(event, primary.path)}>{primary.label}</a>
        </div>
      </header>

      <section className="cd-home-cockpit__mobile-summary" aria-label="Resumen del día">
        <article className="cd-home-cockpit__mobile-hero">
          <p className="cd-home-cockpit__mobile-hero-label">Prioridad del día</p>
          <p className="cd-home-cockpit__mobile-hero-amount">{primary.value}</p>
          <p className="cd-home-cockpit__mobile-hero-meta">{primary.meta}</p>
        </article>
        <div className="cd-home-cockpit__mobile-stats" aria-label="Indicadores del día">
          <article className="cd-home-cockpit__mobile-stat"><span>Turnos hoy</span><strong>{overview.metrics.appointmentsToday}</strong></article>
          <article className="cd-home-cockpit__mobile-stat"><span>Conversaciones</span><strong>{overview.metrics.openConversations}</strong></article>
          <article className="cd-home-cockpit__mobile-stat"><span>Próximos</span><strong>{overview.metrics.upcomingAppointments}</strong></article>
        </div>
      </section>

      <section className="cd-home-cockpit__kpi-strip" aria-label="Métricas operativas">
        <article className="cd-home-cockpit__kpi-tile"><SalonIcon name="calendar_today" className="cd-salons-visual-icon cd-salons-visual-icon--warm" /><span>Turnos hoy</span><strong>{overview.metrics.appointmentsToday}</strong></article>
        <article className="cd-home-cockpit__kpi-tile"><SalonIcon name="schedule" className="cd-salons-visual-icon cd-salons-visual-icon--neutral" /><span>Próximos turnos</span><strong>{overview.metrics.upcomingAppointments}</strong></article>
        <article className="cd-home-cockpit__kpi-tile"><SalonIcon name="chat_bubble" className="cd-salons-visual-icon cd-salons-visual-icon--violet" /><span>Conversaciones abiertas</span><strong>{overview.metrics.openConversations}</strong></article>
        <article className="cd-home-cockpit__kpi-tile"><SalonIcon name="group" className="cd-salons-visual-icon cd-salons-visual-icon--warm" /><span>Clientes</span><strong>{overview.metrics.customers}</strong></article>
      </section>

      <section className="cd-page-section" aria-labelledby="salons-priorities-title">
        <div className="cd-page-section__head">
          <div><h2 id="salons-priorities-title">Prioridades de hoy</h2><p>Lo que conviene resolver primero.</p></div>
        </div>
        {priorities.length === 0
          ? <div className="cd-home-empty-priority"><strong>Todo al día</strong><span>No hay señales operativas urgentes con los datos disponibles.</span></div>
          : <div className="cd-home-priority-grid">
              {priorities.slice(0, 3).map((item) => <a
                key={item.key}
                href={item.path}
                className={`cd-home-priority-card${item.tone === "urgent" ? " cd-home-priority-card--urgent" : ""}`}
                onClick={(event) => link(event, item.path)}
              >
                <SalonIcon name={item.path.endsWith("/conversations") ? "forum" : item.path.endsWith("/agenda") ? "calendar_today" : item.path.endsWith("/sales") ? "payments" : "person"} className="cd-salons-priority-icon" /><span className="cd-home-priority-card__badge">{item.badge}</span>
                <strong>{item.title}</strong>
                <span>{item.description}</span>
                <span className="cd-home-priority-card__cta">{item.cta}<span aria-hidden="true"> →</span></span>
              </a>)}
            </div>}
      </section>

      <section className="cd-page-section" aria-labelledby="salons-today-title">
        <div className="cd-page-section__head">
          <div><h2 id="salons-today-title">Hoy en tu negocio</h2><p>Agenda, conversaciones y estado de cobros.</p></div>
        </div>
        <div className="cd-home-panels">
          <section className="cd-home-panel" aria-labelledby="salons-home-agenda">
            <div className="cd-home-panel__head">
              <div><h3 id="salons-home-agenda">Agenda de hoy</h3><p>Próximos turnos con cliente, servicio y horario.</p></div>
              <a href={`${base}/agenda`} onClick={(event) => link(event, `${base}/agenda`)}>Ver agenda <span aria-hidden="true">→</span></a>
            </div>
            <ul>
              {todayAppointments.length === 0
                ? <li className="cd-home-panel__empty">Sin turnos programados para hoy.</li>
                : todayAppointments.slice(0, 4).map((item) => <li key={item.id}>
                    <div><strong>{item.customerName}</strong><span>{item.serviceName}</span></div>
                    <time dateTime={item.startsAt}>{timeOnly(item.startsAt, overview.timeZone)}</time>
                  </li>)}
            </ul>
          </section>

          <section className="cd-home-panel" aria-labelledby="salons-home-inbox">
            <div className="cd-home-panel__head">
              <div><h3 id="salons-home-inbox">Conversaciones abiertas</h3><p>Conversaciones abiertas del negocio.</p></div>
              <a href={`${base}/conversations`} onClick={(event) => link(event, `${base}/conversations`)}>Ver <span aria-hidden="true">→</span></a>
            </div>
            <ul>
              {openConversations.length === 0
                ? <li className="cd-home-panel__empty">No hay conversaciones abiertas.</li>
                : openConversations.slice(0, 4).map((item) => <li key={item.id}>
                    <div><strong>{item.customerName}</strong><span>{item.latestBody ?? "Sin mensajes"}</span></div>
                    <small>{item.channel}</small>
                  </li>)}
            </ul>
          </section>

          <section className="cd-home-panel" aria-labelledby="salons-home-sales">
            <div className="cd-home-panel__head">
              <div><h3 id="salons-home-sales">Cobros pendientes</h3><p>Ventas registradas en el ledger canónico.</p></div>
              <a href={`${base}/sales`} onClick={(event) => link(event, `${base}/sales`)}>Ver Ventas <span aria-hidden="true">→</span></a>
            </div>
            <div className={"cd-home-domain-status" + (mercadoPagoNeedsAttention ? " cd-home-domain-status--attention" : "")}>
              <span className="cd-home-domain-status__badge">{mercadoPagoLabel}</span>
              {mercadoPago?.connected && mercadoPago.connection && <p>
                Provider · {mercadoPago.connection.liveMode ? "producción" : "sandbox"} · estado {mercadoPago.connection.status}
              </p>}
              {mercadoPagoError && <p>{mercadoPagoError}</p>}
            </div>
            {overview.metrics.salesByCurrency.length === 0
              ? <div className="cd-home-domain-status"><span className="cd-home-domain-status__badge">Sin ventas registradas</span><p>Cuando se cree una venta desde un turno, sus cobros aparecerán acá.</p></div>
              : <ul>
                  {overview.metrics.salesByCurrency.map((item) => <li key={item.currency}>
                    <div>
                      <strong>{money(item.pendingAmountMinor, item.currency)} pendientes</strong>
                      <span>{money(item.collectedAmountMinor, item.currency)} cobrados</span>
                    </div>
                    <small>{countLabel(item.pendingCount, "venta pendiente", "ventas pendientes")}</small>
                  </li>)}
                </ul>}
          </section>
        </div>
      </section>


    </div>
  </section>;
}
